import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { gql } from "graphql-request";
import { graphqlClient } from "@/lib/graphql";
import { LocationAutocomplete } from "@/components/ui/LocationAutocomplete";
import type { Listing, DeliveryPriceSuggestion, BoundingBox } from "@/types";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { DeliveryPriceSuggestions } from "@/components/listings/DeliveryPriceSuggestions";
import { PriceDetailsPopover, getConditionAdjustedPrice } from "@/components/listings/PriceDetailsPopover";
import { BoundingBoxOverlay } from "@/components/listings/BoundingBoxOverlay";

const LISTING_QUERY = gql`
  query Listing($id: ID!) {
    listing(id: $id) {
      id
      price
      condition
      conditionNotes
      language
      shippingAvailable
      shippingCost
      location
      deliveryAvailable
      pickupAvailable
      pickupCity
      pickupLatitude
      pickupLongitude
      pickupRadius
      displayTitle
      displayImageUrl
      imageUrls
      boundingBox {
        yMin
        xMin
        yMax
        xMax
      }
      siblingListings {
        id
        status
        gameTitle
        price
        boundingBox {
          yMin
          xMin
          yMax
          xMax
        }
      }
      game {
        id
        title
        yearPublished
        thumbnailUrl
        languageIndependent
        suggestedPrice
        suggestedPriceInfo {
          price
          newPrice
          shopName
        }
        shopListings {
          id
          shopName
          listingUrl
          price
          listingName
        }
      }
    }
  }
`;

const UPDATE_LISTING_MUTATION = gql`
  mutation UpdateListing(
    $id: ID!
    $price: Float
    $condition: String
    $conditionNotes: String
    $language: String
    $shippingAvailable: Boolean
    $shippingCost: Float
    $location: String
    $deliveryAvailable: Boolean
    $pickupAvailable: Boolean
    $pickupCity: String
    $pickupLatitude: Float
    $pickupLongitude: Float
    $pickupRadius: Int
    $bboxYMin: Int
    $bboxXMin: Int
    $bboxYMax: Int
    $bboxXMax: Int
    $clearBoundingBox: Boolean
  ) {
    updateListing(
      id: $id
      price: $price
      condition: $condition
      conditionNotes: $conditionNotes
      language: $language
      shippingAvailable: $shippingAvailable
      shippingCost: $shippingCost
      location: $location
      deliveryAvailable: $deliveryAvailable
      pickupAvailable: $pickupAvailable
      pickupCity: $pickupCity
      pickupLatitude: $pickupLatitude
      pickupLongitude: $pickupLongitude
      pickupRadius: $pickupRadius
      bboxYMin: $bboxYMin
      bboxXMin: $bboxXMin
      bboxYMax: $bboxYMax
      bboxXMax: $bboxXMax
      clearBoundingBox: $clearBoundingBox
    ) {
      listing {
        id
      }
      errors
    }
  }
`;

const GAME_DELIVERY_SUGGESTIONS_QUERY = gql`
  query GameDeliverySuggestions($id: ID!) {
    game(id: $id) {
      deliveryPriceSuggestions {
        carrier
        tier
        rawPrice
        suggestedPrice
        recommended
      }
    }
  }
`;

const CONDITION_KEYS = ["new", "like_new", "good", "acceptable"] as const;

const PRIORITY_LANGUAGES = ["universal", "en", "lt", "ru"];
const OTHER_LANGUAGES = ["de", "fr", "pl", "es", "it", "nl", "pt", "zh", "ja", "ko"];
const LANGUAGE_LABELS: Record<string, string> = {
  universal: "Universali (nuo kalbos nepriklausomas)",
  en: "Anglų",
  lt: "Lietuvių",
  ru: "Rusų",
  de: "Vokiečių",
  fr: "Prancūzų",
  pl: "Lenkų",
  es: "Ispanų",
  it: "Italų",
  nl: "Olandų",
  pt: "Portugalų",
  zh: "Kinų",
  ja: "Japonų",
  ko: "Korėjiečių",
};

export function EditListingPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [price, setPrice] = useState("");
  const [condition, setCondition] = useState("good");
  const [conditionNotes, setConditionNotes] = useState("");
  const [language, setLanguage] = useState("lt");
  const [shippingAvailable, setShippingAvailable] = useState(true);
  const [shippingCost, setShippingCost] = useState("");
  const [location, setLocation] = useState("");
  const [errors, setErrors] = useState<string[]>([]);
  const [initialized, setInitialized] = useState(false);
  const [boundingBox, setBoundingBox] = useState<BoundingBox | null>(null);
  const [boundingBoxDirty, setBoundingBoxDirty] = useState(false);

  // Delivery & Pickup
  const [deliveryAvailable, setDeliveryAvailable] = useState(true);
  const [pickupAvailable, setPickupAvailable] = useState(true);
  const [pickupCity, setPickupCity] = useState("");
  const [pickupLatitude, setPickupLatitude] = useState<number | null>(null);
  const [pickupLongitude, setPickupLongitude] = useState<number | null>(null);
  const [pickupRadius, setPickupRadius] = useState("0");

  const { data, isLoading } = useQuery({
    queryKey: ["listingEdit", id],
    queryFn: () =>
      graphqlClient.request<{ listing: Listing | null }>(LISTING_QUERY, { id }),
    enabled: !!id,
  });

  const listing = data?.listing;

  const { data: deliverySuggestionsData } = useQuery({
    queryKey: ["gameDeliverySuggestions", listing?.game?.id],
    queryFn: () =>
      graphqlClient.request<{
        game: { deliveryPriceSuggestions: DeliveryPriceSuggestion[] };
      }>(GAME_DELIVERY_SUGGESTIONS_QUERY, { id: listing!.game!.id }),
    enabled: !!listing?.game?.id && deliveryAvailable,
  });

  const deliverySuggestions =
    deliverySuggestionsData?.game?.deliveryPriceSuggestions ?? [];

  useEffect(() => {
    if (listing && !initialized) {
      setPrice(listing.price != null ? String(listing.price) : "");
      setCondition(listing.condition);
      setConditionNotes(listing.conditionNotes || "");
      setLanguage(listing.game?.languageIndependent ? "universal" : (listing.language || "lt"));
      setShippingAvailable(listing.shippingAvailable);
      setShippingCost(listing.shippingCost != null ? String(listing.shippingCost) : "");
      setLocation(listing.location || "");
      setDeliveryAvailable(listing.deliveryAvailable ?? true);
      setPickupAvailable(listing.pickupAvailable ?? false);
      setPickupCity(listing.pickupCity || "");
      setPickupLatitude(listing.pickupLatitude ?? null);
      setPickupLongitude(listing.pickupLongitude ?? null);
      setPickupRadius(String(listing.pickupRadius ?? 0));
      setBoundingBox(listing.boundingBox ?? null);
      setBoundingBoxDirty(false);
      setInitialized(true);
    }
  }, [listing, initialized]);

  const updateMutation = useMutation({
    mutationFn: (vars: {
      id: string;
      price?: number;
      condition?: string;
      conditionNotes?: string;
      language?: string;
      shippingAvailable?: boolean;
      shippingCost?: number;
      location?: string;
      deliveryAvailable?: boolean;
      pickupAvailable?: boolean;
      pickupCity?: string;
      pickupLatitude?: number;
      pickupLongitude?: number;
      pickupRadius?: number;
      bboxYMin?: number;
      bboxXMin?: number;
      bboxYMax?: number;
      bboxXMax?: number;
      clearBoundingBox?: boolean;
    }) =>
      graphqlClient.request<{ updateListing: { listing: Listing | null; errors: string[] } }>(
        UPDATE_LISTING_MUTATION,
        vars
      ),
    onSuccess: (data) => {
      if (data.updateListing.listing) {
        const newId = data.updateListing.listing.id;
        queryClient.removeQueries({ queryKey: ["listing", id] });
        queryClient.removeQueries({ queryKey: ["listing", newId] });
        queryClient.removeQueries({ queryKey: ["listingEdit", id] });
        queryClient.removeQueries({ queryKey: ["listingEdit", newId] });
        queryClient.invalidateQueries({ queryKey: ["listings"] });
        queryClient.invalidateQueries({ queryKey: ["myListings"] });
        navigate(`/listings/${newId}`);
      } else {
        setErrors(data.updateListing.errors);
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors([]);

    if (!id) return;

    const priceNum = parseFloat(price);
    if (isNaN(priceNum) || priceNum <= 0) {
      setErrors([t("listings.validation_invalid_price")]);
      return;
    }

    if (!deliveryAvailable && !pickupAvailable) {
      setErrors([t("listings.validation_delivery_method")]);
      return;
    }

    updateMutation.mutate({
      id,
      price: priceNum,
      condition,
      conditionNotes: conditionNotes || undefined,
      language: language || undefined,
      shippingAvailable,
      shippingCost: shippingAvailable && shippingCost ? parseFloat(shippingCost) : undefined,
      location: location || undefined,
      deliveryAvailable,
      pickupAvailable,
      pickupCity: pickupAvailable ? pickupCity || undefined : undefined,
      pickupLatitude: pickupAvailable && pickupLatitude != null ? pickupLatitude : undefined,
      pickupLongitude: pickupAvailable && pickupLongitude != null ? pickupLongitude : undefined,
      pickupRadius: pickupAvailable ? parseInt(pickupRadius, 10) : undefined,
      ...(boundingBoxDirty
        ? boundingBox
          ? {
              bboxYMin: boundingBox.yMin,
              bboxXMin: boundingBox.xMin,
              bboxYMax: boundingBox.yMax,
              bboxXMax: boundingBox.xMax,
            }
          : { clearBoundingBox: true }
        : {}),
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner />
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-8 text-center sm:px-6 lg:px-8">
        <p className="text-gray-500">{t("listings.not_found")}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6 lg:px-8">
      <h1 className="mb-8 text-2xl font-bold text-gray-900">{t("listings.edit_title")}</h1>

      {errors.length > 0 && (
        <div className="mb-6 rounded-lg bg-red-50 p-3 text-sm text-red-700">
          {errors.map((err, i) => (
            <p key={i}>{err}</p>
          ))}
        </div>
      )}

      {/* Game info (read-only) */}
      <div className="mb-6 flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
        {listing.displayImageUrl && (
          <img
            src={listing.displayImageUrl}
            alt={listing.displayTitle}
            className="h-12 w-12 rounded object-cover"
          />
        )}
        <div>
          <p className="font-medium text-gray-900">{listing.displayTitle}</p>
          {listing.game?.yearPublished && (
            <p className="text-sm text-gray-500">{listing.game.yearPublished}</p>
          )}
        </div>
      </div>

      {/* Bounding box editor */}
      {listing.imageUrls.length > 0 && (
        <div className="mb-6">
          <label className="mb-2 block text-sm font-medium text-gray-700">
            {t("listings.photo_region")}
          </label>
          <BoundingBoxOverlay
            mode="edit"
            activeBoundingBox={boundingBox}
            siblings={(listing.siblingListings ?? []).filter((s) => s.boundingBox)}
            imageUrl={listing.imageUrls[0]}
            readOnlySiblings
            onAddBox={(box) => {
              setBoundingBox(box);
              setBoundingBoxDirty(true);
            }}
            onRemoveSibling={() => {}}
            onRemoveCurrentBox={() => {
              setBoundingBox(null);
              setBoundingBoxDirty(true);
            }}
            onUpdateActiveBox={(box) => {
              setBoundingBox(box);
              setBoundingBoxDirty(true);
            }}
            onUpdateSiblingBox={() => {}}
          />
          <p className="mt-1 text-xs text-gray-500">
            {t("listings.photo_region_hint")}
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Condition */}
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            {t("listings.condition")}
          </label>
          <select
            value={condition}
            onChange={(e) => setCondition(e.target.value)}
            className="input"
          >
            {CONDITION_KEYS.map((key) => (
              <option key={key} value={key}>
                {t(`conditions.${key}`)}
              </option>
            ))}
          </select>
        </div>

        {/* Condition notes */}
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            {t("listings.condition_notes")}
          </label>
          <textarea
            value={conditionNotes}
            onChange={(e) => setConditionNotes(e.target.value)}
            rows={3}
            placeholder={t("listings.condition_notes_placeholder")}
            className="input"
          />
        </div>

        {/* Language */}
        {!listing.game?.languageIndependent && (
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              {t("listings.language")}
            </label>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="input"
            >
              {PRIORITY_LANGUAGES.map((code) => (
                <option key={code} value={code}>
                  {LANGUAGE_LABELS[code]}
                </option>
              ))}
              <option disabled>──────────</option>
              {OTHER_LANGUAGES.map((code) => (
                <option key={code} value={code}>
                  {LANGUAGE_LABELS[code]}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Price */}
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            {t("listings.price_label")}
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="0.00"
            className="input"
            required
          />
          {listing.game?.suggestedPriceInfo && (
            <p className="mt-1 flex items-center gap-1.5 text-sm text-gray-500">
              <span>{t("listings.suggested_price_label")}:</span>
              <button
                type="button"
                className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                onClick={() =>
                  setPrice(
                    String(
                      getConditionAdjustedPrice(
                        listing.game!.suggestedPriceInfo!.newPrice,
                        condition,
                      ),
                    ),
                  )
                }
              >
                {(() => {
                  const adjusted = getConditionAdjustedPrice(
                    listing.game!.suggestedPriceInfo!.newPrice,
                    condition,
                  );
                  return adjusted % 1 === 0 ? `${adjusted}` : adjusted.toFixed(2);
                })()}{" "}
                &euro;
              </button>
              {listing.game?.shopListings && listing.game.shopListings.length > 0 && (
                <PriceDetailsPopover
                  shopListings={listing.game.shopListings}
                  suggestedPriceInfo={listing.game.suggestedPriceInfo}
                  condition={condition}
                  onSelectPrice={(p) => setPrice(String(p))}
                />
              )}
            </p>
          )}
        </div>

        {/* Delivery & Pickup options */}
        <div>
          <label className="mb-3 block text-sm font-semibold text-gray-900">
            {t("listings.delivery_methods")}
          </label>
          <p className="mb-3 text-xs text-gray-500">
            {t("listings.delivery_methods_hint")}
          </p>

          <div className="space-y-4">
            {/* Delivery toggle */}
            <div>
              <div className="flex items-center gap-3">
                <label className="relative inline-flex cursor-pointer items-center">
                  <input
                    type="checkbox"
                    checked={deliveryAvailable}
                    onChange={(e) => {
                      setDeliveryAvailable(e.target.checked);
                      setShippingAvailable(e.target.checked);
                    }}
                    className="peer sr-only"
                  />
                  <div className="h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary-800 peer-checked:after:translate-x-full peer-checked:after:border-white" />
                </label>
                <span className="text-sm font-medium text-gray-700">
                  {t("listings.delivery_available")}
                </span>
              </div>

              {deliveryAvailable && (
                <div className="ml-14 mt-3">
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    {t("listings.shipping_cost_label")}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={shippingCost}
                    onChange={(e) => setShippingCost(e.target.value)}
                    placeholder="0.00"
                    className="input w-40"
                  />
                  <DeliveryPriceSuggestions
                    suggestions={deliverySuggestions}
                    onSelect={(price) => setShippingCost(String(price))}
                  />
                </div>
              )}
            </div>

            {/* Pickup toggle */}
            <div>
              <div className="flex items-center gap-3">
                <label className="relative inline-flex cursor-pointer items-center">
                  <input
                    type="checkbox"
                    checked={pickupAvailable}
                    onChange={(e) => setPickupAvailable(e.target.checked)}
                    className="peer sr-only"
                  />
                  <div className="h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary-800 peer-checked:after:translate-x-full peer-checked:after:border-white" />
                </label>
                <span className="text-sm font-medium text-gray-700">
                  {t("listings.pickup_available")}
                </span>
              </div>

              {pickupAvailable && (
                <div className="ml-14 mt-3 space-y-3">
                  <div>
                    <label className="mb-1 block text-sm text-gray-600">
                      {t("listings.pickup_radius")}
                    </label>
                    <select
                      value={pickupRadius}
                      onChange={(e) => setPickupRadius(e.target.value)}
                      className="input w-40"
                    >
                      <option value="0">{t("listings.pickup_radius_city_only")}</option>
                      <option value="1">1 km</option>
                      <option value="2">2 km</option>
                      <option value="3">3 km</option>
                      <option value="5">5 km</option>
                      <option value="10">10 km</option>
                      <option value="15">15 km</option>
                      <option value="-1">{t("listings.pickup_radius_all_lithuania")}</option>
                    </select>
                  </div>

                  <LocationAutocomplete
                    label={t("listings.pickup_location")}
                    value={pickupCity}
                    latitude={pickupLatitude}
                    longitude={pickupLongitude}
                    radius={Number(pickupRadius)}
                    onChange={(data) => {
                      setPickupCity(data.city || data.address);
                      setPickupLatitude(data.latitude);
                      setPickupLongitude(data.longitude);
                      setLocation(data.city || data.address);
                    }}
                    placeholder={t("listings.pickup_location_placeholder")}
                  />
                </div>
              )}
            </div>
          </div>

          {!deliveryAvailable && !pickupAvailable && (
            <p className="mt-2 text-sm text-red-600">
              {t("listings.validation_delivery_method")}
            </p>
          )}
        </div>

        {/* Location (legacy, still visible) */}
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            {t("listings.location")}
          </label>
          <input
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder={t("listings.location_placeholder")}
            className="input"
          />
        </div>

        <button
          type="submit"
          disabled={updateMutation.isPending}
          className="btn-primary w-full"
        >
          {updateMutation.isPending ? t("listings.updating") : t("listings.update_button")}
        </button>
      </form>
    </div>
  );
}
